import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Интерфейс для компонентов, которым нужно выполнить асинхронные операции перед синхронизацией формы
 */
export interface BeforeSyncHandler {
  /**
   * Уникальный идентификатор обработчика (полезен для отладки)
   */
  handlerId: string;

  /**
   * Асинхронная функция для выполнения перед синхронизацией
   * Должна резолвиться после завершения операции
   */
  handler: () => Promise<void>;
}

/**
 * Сервис для координации операций синхронизации формы с дочерними компонентами
 * Позволяет дочерним компонентам регистрировать асинхронные обработчики, которые должны
 * завершиться до того, как родительская форма продолжит операцию синхронизации
 *
 * @example
 * // В дочернем компоненте:
 * constructor() {
 *   const coordinator = inject(WorkflowFormSyncCoordinatorService);
 *   coordinator.registerHandler({
 *     handlerId: 'actualAddressUpdate',
 *     handler: () => this.updateAddress()
 *   });
 * }
 *
 * // В родительском компоненте:
 * async sync() {
 *   await this.coordinator.executeBeforeSyncHandlers();
 *   // Продолжить синхронизацию...
 * }
 */
@Injectable()
export class WorkflowFormSyncCoordinatorService implements OnDestroy {
  private handlers = new Map<string, () => Promise<void>>();
  private syncTrigger$ = new Subject<void>();

  /**
   * Зарегистрировать обработчик, который будет вызван перед синхронизацией
   * @param config Конфигурация обработчика с уникальным ID и асинхронной функцией
   */
  registerHandler(config: BeforeSyncHandler): void {
    if (this.handlers.has(config.handlerId)) {
      console.warn(
        `Обработчик с id "${config.handlerId}" уже зарегистрирован. Он будет перезаписан.`,
      );
    }

    this.handlers.set(config.handlerId, config.handler);
  }

  /**
   * Выполнить все зарегистрированные обработчики параллельно
   * Возвращает promise, который резолвится после завершения всех обработчиков
   * @throws Выбросит ошибку, если хотя бы один обработчик отклонится
   */
  async executeBeforeSyncHandlers(): Promise<void> {
    if (this.handlers.size === 0) {
      return;
    }

    try {
      const promises = Array.from(this.handlers.values()).map(handler =>
        handler(),
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('Ошибка при выполнении before-sync обработчиков:', error);
      throw error;
    }
  }

  /**
   * Очистить все зарегистрированные обработчики
   */
  clearAllHandlers(): void {
    this.handlers.clear();
  }

  ngOnDestroy(): void {
    this.clearAllHandlers();
    this.syncTrigger$.complete();
  }
}
